import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Sparkles, Clock, Users, Shield, Heart, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TrialSignupModal from '../components/TrialSignupModal';

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const AboutPage = () => {
  const navigate = useNavigate();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  const values = [
    {
      icon: Clock,
      title: "Time is everything",
      description: "Every feature we build must give users time back, not consume it."
    },
    {
      icon: Sparkles,
      title: "Simple is powerful",
      description: "Complex problems don't require complex solutions. They require elegant ones."
    },
    {
      icon: Heart,
      title: "Honest > Polished",
      description: "We'd rather be real about where we are than fake about where we're not."
    },
    {
      icon: Users,
      title: "Users are partners",
      description: "You're not buying from us. You're building with us. Your success is literally our success."
    }
  ];

  const team = [
    {
      name: "Sarah Chen",
      role: "Founder & CEO",
      bio: "I built DoubleMe after missing a $100,000 contract because I was in another meeting. Now my double handles discovery calls while I focus on building the future.",
      avatar: "SC"
    },
    {
      name: "Marcus Kim",
      role: "Engineering",
      bio: "Former burnout victim turned builder of burnout prevention. I code so others don't have to choose between career and life.",
      avatar: "MK"
    },
    {
      name: "Jennifer Rodriguez",
      role: "Design",
      bio: "I believe technology should give us time back, not take it. Every pixel I design is measured in minutes returned to users.",
      avatar: "JR"
    },
    {
      name: "David Park",
      role: "AI/ML",
      bio: "Teaching machines to be more human so humans can be more human. The irony isn't lost on me.",
      avatar: "DP"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/')} className="text-2xl font-bold text-gray-900">
              DoubleMe
            </button>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/pricing')} className="text-gray-600 hover:text-gray-900 font-medium transition">
                Pricing
              </button>
              <button onClick={() => navigate('/experience')} className="text-gray-600 hover:text-gray-900 font-medium transition">
                Experience
              </button>
              <button
                onClick={() => setShowOnboardingModal(true)}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={slideUp} className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Why I built DoubleMe
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-12"
          >
            {/* The Breaking Point */}
            <motion.div variants={slideUp} className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">The breaking point</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                It was 7:43pm on a Tuesday. I was on my third conference call of the evening, missing another family dinner. My daughter had drawn a picture of our family at the dinner table—with my chair empty and my phone in my place.
              </p>
              <p className="text-gray-900 font-semibold text-xl">
                That hit hard.
              </p>
              <p className="text-gray-700 leading-relaxed">
                As a consultant, I was drowning. Client calls at all hours. Prospects in different time zones. Support questions I'd answered a hundred times. I was everywhere and nowhere at the same time. Present for everyone except the people who mattered most.
              </p>
            </motion.div>

            {/* The Realization */}
            <motion.div variants={slideUp} className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">The realization</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                90% of my calls were repetitive. The same questions about my services. The same discovery process. The same troubleshooting steps. I wasn't adding unique value—I was a human answering machine.
              </p>
              <p className="text-gray-700 leading-relaxed">
                But here's the thing: those calls mattered. Every missed call was a missed opportunity. Every delayed response was a frustrated client. I couldn't clone myself, but maybe... technology could.
              </p>
            </motion.div>

            {/* The Journey */}
            <motion.div variants={slideUp} className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">The journey</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                I spent six months building the first version. Not perfect, but functional. My first double handled five calls while I was at my daughter's recital. All five callers got their questions answered. None knew it wasn't me.
              </p>
              <p className="text-gray-700 leading-relaxed">
                That's when I knew this wasn't just my problem. Every professional I talked to was drowning in the same way. Talented people reduced to repeating themselves instead of doing what they do best.
              </p>
            </motion.div>

            {/* Where We Are Today */}
            <motion.div variants={slideUp} className="bg-primary/5 border border-dark rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Where we are today</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                DoubleMe is in early access. We have a working product that's transforming how our early users work and live. It's not perfect—we're still building, still learning, still improving. But it works. Really works.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We don't have thousands of customers yet. We don't have case studies or testimonials plastered everywhere. What we have is a solution to a real problem that millions of professionals face every day.
              </p>
            </motion.div>

            {/* Where We're Going */}
            <motion.div variants={slideUp} className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Where we're going</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our vision is simple: give every professional the ability to be present wherever they're needed, without sacrificing their actual presence where it matters most.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">We're building toward a future where:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span>No opportunity is missed because you were unavailable</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span>No relationship suffers because work demanded your presence</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span>No one burns out from being pulled in too many directions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span>Everyone can scale their impact without scaling their hours</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Join Us Early */}
      <section className="py-20 bg-gradient-to-b from-white to-accent/5">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={slideUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary rounded-full text-primary font-medium">
                <Sparkles className="w-4 h-4" />
                Join us early
              </span>
            </motion.div>

            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-6">
              Early isn't just a discount. It's an advantage.
            </motion.h2>

            <motion.div variants={slideUp} className="prose prose-lg max-w-none text-gray-700 mb-12">
              <p className="text-xl leading-relaxed mb-6">
                If you're reading this, you're early. Early to a product that we believe will fundamentally change how professionals work. Early enough to shape what we build.
              </p>
              <p className="text-xl leading-relaxed mb-6">
                We're not asking you to trust our track record—we don't have one yet. We're asking you to try the product, experience the transformation, and help us make it even better.
              </p>
              <p className="text-xl leading-relaxed">
                Your feedback shapes our roadmap. Your use cases drive our features. Your success is literally our success.
              </p>
            </motion.div>

            <motion.button
              variants={slideUp}
              onClick={() => setShowOnboardingModal(true)}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition flex items-center gap-2 mx-auto"
            >
              Join Early Access <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={slideUp} className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">The team making it happen</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We're a small team of engineers and designers who've felt this problem firsthand.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  variants={slideUp}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary/50 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                      {member.avatar}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                      <div className="text-primary font-medium mb-3">{member.role}</div>
                      <p className="text-gray-700 leading-relaxed italic">"{member.bio}"</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 text-center mb-12">
              What we believe
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={slideUp}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:border-secondary transition"
                >
                  <value.icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-700">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founder Letter */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="bg-gradient-to-br from-dark to-dark rounded-2xl p-8 md:p-12 text-white"
          >
            <motion.div variants={slideUp} className="mb-8">
              <div className="text-white/60 text-sm font-medium mb-2">A Letter from the Founder</div>
              <h2 className="text-3xl font-bold">Dear Future DoubleMe User,</h2>
            </motion.div>

            <motion.div variants={slideUp} className="space-y-4 text-lg leading-relaxed">
              <p>
                I'm writing this at 11 PM, after putting my kids to bed. My double handled seven calls today while I was present for my family. This is what DoubleMe means to me.
              </p>
              <p>
                It's not about the technology (though it's incredible). It's not about the efficiency (though you'll 10x your reach). It's about the freedom to be human while being superhuman.
              </p>
              <p>
                Your double isn't replacing you. It's the superhero version of you that handles the repetitive so you can focus on the remarkable.
              </p>
              <p className="text-xl font-semibold text-white/80">
                Welcome to being everywhere at once. Welcome to having a life and a career. Welcome to the future you've been waiting for.
              </p>
            </motion.div>

            <motion.div variants={slideUp} className="mt-8 pt-6 border-t border-primary">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-lg">Sarah Chen</div>
                  <div className="text-white/60">Founder & CEO, DoubleMe</div>
                </div>
                <a
                  href="mailto:sarah@doubleme.ai"
                  className="flex items-center gap-2 text-white/80 hover:text-white transition"
                >
                  <Mail className="w-5 h-5" />
                  <span className="hidden sm:inline">sarah@doubleme.ai</span>
                </a>
              </div>
            </motion.div>

            <motion.div variants={slideUp} className="mt-6 pt-6 border-t border-primary text-center">
              <p className="mb-4">P.S. Call my double anytime: <span className="font-mono font-semibold">+1 (629) 230-9527</span></p>
              <p className="text-white/60">Seriously. Right now. Experience the future firsthand.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold mb-4">
              Ready to join the movement?
            </motion.h2>
            <motion.p variants={slideUp} className="text-xl mb-8 opacity-90">
              Start your 14-day free trial. No credit card required.
            </motion.p>
            <motion.button
              variants={slideUp}
              onClick={() => setShowOnboardingModal(true)}
              className="bg-white hover:bg-gray-100 text-primary px-8 py-4 rounded-full font-semibold text-lg transition"
            >
              Create Your First Double
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Onboarding Modal */}
      {/* Trial Signup Modal */}
      <TrialSignupModal isOpen={showOnboardingModal} onClose={() => setShowOnboardingModal(false)} />
    </div>
  );
};

export default AboutPage;
